import React, { useCallback, forwardRef, useState, useRef, useMemo } from 'react';
import { isNil } from 'lodash';
import { useToast } from 'shared/toast';
import { getDateStringForRequest } from 'src/shared/helpers/date';
// Components
import ToggleButton from './ToggleButton';
// eslint-disable-next-line no-unused-vars
import { DatePicker, DatePickerProps } from '@progress/kendo-react-dateinputs';
import ReadOnlyDate from './ReadOnlyDate';
import BaseDateInput from './BaseDateInput';
import CustomDateInput from './CustomDateInput';

const numericRegex = /^(-?\d+)$/;
const dateOnlyRegex = /^\d{4}-\d{2}-\d{2}$/;
const dateRegex = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}:\d{2}Z?$/;

const buildValue = (value) => {
  if (typeof value === 'string') {
    if (numericRegex.test(value)) {
      return value;
    }
    if (dateOnlyRegex.test(value)) {
      return new Date(`${value}T00:00:00`);
    }
    if (dateRegex.test(value)) {
      // Ignore timezone
      return new Date(value.replace('Z', ''));
    }
    return new Date(value);
  }

  return value;
};

/**
 * @typedef {Omit<React.PropsWithoutRef<DatePickerProps>, 'value'>} TelerikDatePickerProps
 *
 * @typedef {Object} AdditionalDatePickerProps
 * @property {boolean} [readOnly={false}] Indicates if the picker is read-only. False by default.
 * @property {number} [width] Sets width of the picker.
 * @property {any} [value] The selected value.
 * @property {boolean} [formatOutputAsDateOnly={false}] Indicates if data should be output as date only. False by default.
 * @property {boolean} [formatOnChange={true}] Indicates if value is formatted on change event to yyyy-mm-ddT00:00:00 (default: true).
 * @property {boolean} [minimal={false}] Indicates if a simple text input without a picker is used. False by default.
 * @property {boolean} [allowNumeric={false}] For minimal={true} only: Indicates if a number can be used as a value. If true, the value represents a number of days from today. False by default.
 * @property {boolean} [stateChangesAfterOnChange={false}] - Indicates if dates warnings are refreshed after each render. False by default.
 * @property {number} [maxDaysWarning] Sets max days warning.
 * @property {number} [minDaysWarning] Sets min days warning.
 * @property {boolean} [allowEmpty={true}] Indicates if value can be deleted or reset. True by default.
 * @property {string} [dataTestId] Data Test ID for auto tests.
 *
 * @param {TelerikDatePickerProps & AdditionalDatePickerProps} props
 * @param {React.Ref<HTMLDivElement>} ref
 * @returns {React.ReactElement}
 */
const BaseDatePicker = (props, ref) => {
  const {
    readOnly = false,
    width,
    onChange,
    defaultValue,
    value,
    formatOnChange = true,
    formatOutputAsDateOnly = false,
    // Minimal version
    allowNumeric = false,
    minimal = false,
    // Warning
    stateChangesAfterOnChange = false,
    maxDaysWarning,
    minDaysWarning,
    // Rest
    dataTestId,
    allowEmpty = true,
    ...rest
  } = props;

  const toast = useToast();

  // Clean value from timezone info
  const dateValue = useMemo(() => buildValue(isNil(value) ? defaultValue : value), [defaultValue, value]);

  // Change handler
  const handleChange = useCallback((e) => {
    const { value, target } = e;

    const innerValue = value ?? (allowEmpty ? null : defaultValue);

    // eslint-disable-next-line no-nested-ternary
    const result = innerValue ? (formatOnChange ? getDateStringForRequest(innerValue, formatOutputAsDateOnly) : innerValue) : null;

    const newEvent = { ...e, value: result };

    if (target) {
      newEvent.target = { ...target, value: result };
    }

    onChange(newEvent);
  }, []);

  const [stateValue, setStateValue] = useState();
  const [warningCss, setWarningCss] = useState('');

  const inputRef = useRef();

  // Minimal version (text input)
  const handleOnChange = useCallback(({ value }) => {
    const mimicEvent = { target: { value }, value };
    handleChange(mimicEvent);
  }, []);

  if (minimal) {
    return (
      <CustomDateInput
        ref={ref}
        {...rest}
        dataTestId={dataTestId}
        allowNumeric={allowNumeric}
        value={dateValue}
        onChange={handleOnChange}
        className='minimal-date-input'
      />
    );
  }

  if (readOnly) {
    return (
      <ReadOnlyDate value={dateValue} data-testid={dataTestId} />
    );
  }

  const doWeNeedaWarning = (d) => {
    if (d) {
      const then = new Date(d);
      const now = new Date().getDate();
      if (maxDaysWarning) {
        const max = new Date().setDate(now + maxDaysWarning);
        if (then > max) {
          return 'future';
        }
      }
      if (minDaysWarning) {
        const min = new Date().setDate(now - minDaysWarning);
        if (then < min) {
          return 'past';
        }
      }
    }
    return '';
  };

  const updateOnEachChange = () => {
    if (dateValue === stateValue) {
      return undefined;
    }

    const warning = doWeNeedaWarning(dateValue);
    if (warning === 'future') {
      return 'max-days-warning';
    } if (warning === 'past') {
      return 'max-days-warning';
    }

    return undefined;
  };

  /**
   * @description - Handles the onChange event of the date picker if datePicker has minDaysWarning or maxDaysWarning property set
   * @param {Object} e - The event object
   */
  const changed = async (e) => {
    setStateValue(e.target.value);
    setWarningCss('');
    const l = doWeNeedaWarning(e.target.value);
    if (l === 'future') {
      setWarningCss('max-days-warning from-component');
      // eslint-disable-next-line max-len
      toast.warning({ message: `The date is more than ${maxDaysWarning} days in the future. Please check your date for typos` });
    } else if (l === 'past') {
      setWarningCss('max-days-warning from-component');
      // eslint-disable-next-line max-len
      toast.warning({ message: `The date is more than ${minDaysWarning} days in the past. Please check your date for typos` });
    }

    handleChange(e);
  };

  if (maxDaysWarning || minDaysWarning) {
    return (
      <span data-testid={dataTestId}>
        <DatePicker
          ref={ref}
          dateInput={(dateInputProps) => (
            <BaseDateInput
              width={width}
              ref={inputRef}
              {...dateInputProps}
              clearButton={allowEmpty}
            />
          )}
          format="MM/dd/yyyy"
          toggleButton={ToggleButton}
          {...rest}
          value={dateValue}
          onChange={changed}
          className={stateChangesAfterOnChange ? updateOnEachChange() : warningCss}
          data-testid={dataTestId}
        />
      </span>
    );
  }

  return (
    <span data-testid={dataTestId}>
      <DatePicker
        ref={ref}
        dateInput={(dateInputProps) => (
          <BaseDateInput
            width={width}
            ref={inputRef}
            {...dateInputProps}
            clearButton={allowEmpty}
          />
        )}
        format="MM/dd/yyyy"
        toggleButton={ToggleButton}
        {...rest}
        value={dateValue}
        onChange={handleChange}
        data-testid={dataTestId}
      />
    </span>
  );
};

/**
 * A base date picker component with various options and handlers.
 *
 * @param {TelerikDatePickerProps & AdditionalDatePickerProps} props - Component props. Extends Telerik DatePicker props.
 * @param {React.Ref<HTMLDivElement>} ref
 * @returns {React.ReactElement} Date Picker component.
 */
const ForwardDatePicker = forwardRef(BaseDatePicker);

export default ForwardDatePicker;
